import {
  fetchTransactionAccount,
  getError,
  getShipToAddress,
  formatShipToAddress,
  isValidAddress,
} from '../../../../transaction-account';
import ChangePricingEligibility from '../../../../transaction-account/src/component/update-pricing-eligibility';
import { SoldToAddressStep, InvoiceContactsStep, CurrencyPONumberStep } from '../billing-profile-components';
import { ApolloQueryResult, useQuery } from '@apollo/client';
import ButtonGroup from '@atlaskit/button/button-group';
import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/standard-button';
import Form, { FormSection, FormHeader, FormFooter } from '@atlaskit/form';
import {
  InfoPanel,
  useFormSubmit,
  useNotification,
  useSidebar,
  successNotificationBuilder,
  ErrorComponents,
  InflightInvoiceBanner,
  NotificationTypes,
} from '@css-xp/base';
import { withApollo } from '@css-xp/base/graphql-provider';
import { CommerceAddress, generatedTypesNew } from '@css-xp/graphtypes';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { updateInvoiceGroup, updateShipToPricingEligibility } from '@atlassian/temp-proxies';
import { UpdateInvoiceGroupRequest, InvoiceGroupPurchaseOrder } from '@atlassian/cssxp-types-billing';
import defaultTo from 'lodash/defaultTo';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import React, { useState } from 'react';
import { HelpContent } from '@css-xp/ppc-common';
import { withErrorWrapper } from '@css-xp/ppc-wrapper';

interface FormData {
  soldToName: string;
  soldToAddress1?: string | null;
  soldToAddress2?: string | null;
  soldToCity?: string | null;
  soldToCountry?: { value: string; label: string };
  soldToPostalCode?: string | null;
  soldToState?: { value: string; label: string };
  soldToTaxId?: string | null;
  soldToGst?: string | null;
  soldToPst?: string | null;
  recipients: string[];
  invoiceGroupName: string;
  purchaseOrderNumber?: string | null;
  pricingPlan?: string | undefined | '';
}

interface Props {
  recipients?: string[];
  id: string;
  shipToParty?: CommerceAddress;

  refetch: () => Promise<ApolloQueryResult<unknown>>;
  transactionAccountId: string;
  name?: string;
  purchaseOrder?: InvoiceGroupPurchaseOrder | null;
  hidePricingEligibilityUpdate?: boolean;
  isPartnerAccount?: boolean;
  onSubmit?: () => Promise<void>;
  inflightBannerMessage?: string;
}
export const EditInvoiceGroupPane = withApollo(
  ({
    recipients,
    id,
    refetch,
    shipToParty,
    transactionAccountId,
    name = '',
    purchaseOrder,
    hidePricingEligibilityUpdate = false,
    isPartnerAccount = false,
    onSubmit,
    inflightBannerMessage = 'All changes will be reflected to the orders included in the inflight invoice',
  }: Props): JSX.Element => {
    const [adminFieldsDirty, setAdminsDirtyFlag] = React.useState<boolean>(true);
    const [isNotValidAddress, setIsNotValidAddress] = useState<boolean>(false);
    const [billingAddressError, setBillingAddressError] = useState<string>('');

    const { data, loading, error } = useQuery<
      generatedTypesNew.FetchTransactionAccountQuery,
      generatedTypesNew.FetchTransactionAccountQueryVariables
    >(fetchTransactionAccount, {
      variables: {
        id: transactionAccountId,
      },
      fetchPolicy: 'no-cache',
    });

    const isPONumberFeatureEnabled = true;

    const transactionAccount = data?.transactionAccount;
    const [isPOMultipleUseChecked, setIsPOMultipleUseChecked] = useState(
      purchaseOrder ? !purchaseOrder.oneTimeUse : false
    );
    const [shipToAddress, setShipToAddress] = React.useState<CommerceAddress | undefined | null>(shipToParty);
    const invoiceRecipients = !isNil(recipients) && recipients?.length === 0 ? [''] : recipients;

    const { closeSidebar } = useSidebar();
    const oldShipTo = {
      shipToParty: {
        name: shipToParty?.name,
        postalAddress: {
          line1: shipToParty?.addressLine1,
          line2: shipToParty?.addressLine2,
          city: shipToParty?.city,
          country: shipToParty?.country,
          state: shipToParty?.state,
          postcode: shipToParty?.postcode,
        },
        taxId: shipToParty?.taxId || null,
        taxIds: shipToParty?.taxIds?.length ? shipToParty?.taxIds : null,
      },
    };

    const { error: formError, isProcessing, submitForm, complete, fieldErrors } = useFormSubmit<
      UpdateInvoiceGroupRequest,
      any
    >({
      submitFormAction: onSubmit ? onSubmit : updateInvoiceGroup,
    });

    const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

    const updateAdminFieldsDirtyFlag = (flag: boolean) => {
      setAdminsDirtyFlag(flag);
    };

    const handleFormSubmit = (formData: FormData) => {
      if (!isValidAddress(formData)) {
        const formError = getError(formData);
        setBillingAddressError(formError);
        setIsNotValidAddress(true);
        return;
      } else {
        setIsNotValidAddress(false);
      }

      let newShipTo;
      newShipTo = {
        shipToParty: getShipToAddress(formData),
      };

      const existingShipTo = {
        shipToParty: {
          name: shipToAddress?.name,
          postalAddress: {
            line1: shipToAddress?.addressLine1,
            line2: shipToAddress?.addressLine2,
            city: shipToAddress?.city,
            country: shipToAddress?.country,
            state: shipToAddress?.state,
            postcode: shipToAddress?.postcode,
          },
          taxId: shipToAddress?.taxId || null,
          taxIds: shipToAddress?.taxIds?.length ? shipToAddress?.taxIds : null,
        },
      };

      // compare the two shipTo's
      if (
        JSON.stringify(formatShipToAddress(oldShipTo.shipToParty)) ===
        JSON.stringify(formatShipToAddress(newShipTo.shipToParty))
      ) {
        // shipToAddress didn't change. Don't send it in the request
        newShipTo = { shipToPartyId: shipToParty?.id ?? null };
      } else if (
        !isEmpty(shipToAddress) &&
        isEqual(formatShipToAddress(existingShipTo.shipToParty), formatShipToAddress(newShipTo.shipToParty)) &&
        shipToAddress?.id
      ) {
        // also check if we selected an existing shipTo
        newShipTo = { shipToPartyId: shipToAddress.id };
      }

      const requestData: UpdateInvoiceGroupRequest = {
        data: {
          ...newShipTo,
          name: formData?.invoiceGroupName,
        },
        invoiceGroupId: id,
        transactionAccountId: transactionAccountId,
      };
      if (isPONumberFeatureEnabled && formData.purchaseOrderNumber) {
        requestData.data.purchaseOrder = {
          number: formData.purchaseOrderNumber,
          oneTimeUse: !isPOMultipleUseChecked,
        };
      }
      const newRecipients = formData?.recipients?.filter((recipient: string) => recipient.length > 0) || [];
      const existingRecipients = invoiceRecipients?.filter((recipient: string) => recipient.length > 0) || [];

      if (
        newRecipients.length < existingRecipients.length ||
        newRecipients.some((recipient) => !existingRecipients.includes(recipient))
      ) {
        requestData.data.recipients = newRecipients;
      }

      if (
        !hidePricingEligibilityUpdate &&
        (shipToParty?.shipToPriceEligibility ?? transactionAccount?.billToPartyPricingEligibilty) !==
          formData?.pricingPlan
      ) {
        submitPricingPlan(formData.pricingPlan as string, newShipTo?.shipToPartyId);
      }
      submitForm(requestData);
    };

    const onPOMultipleUseChangeEvent = () => {
      setIsPOMultipleUseChecked((current) => !current);
    };
    if (complete && !notificationSent) {
      const notificationOptions = successNotificationBuilder()
        .withId('update-invoice-group-success')
        .withHeader(isPartnerAccount ? 'Update successful' : 'Successfully updated billing profile.')
        .withMessage(isPartnerAccount ? 'Billing profile updated' : '')
        .withDismissAfter(5000)
        .isVersion(NotificationTypes.FLAG)
        .build();
      sendSuccessNotification(notificationOptions);
      setNotificationSent(true);
      closeSidebar();
      refetch();
    }

    const submitPricingPlan = (pricingPlan: string, existingShipToPartyId: string | null | undefined) => {
      const pricingEligibility = {
        [pricingPlan]: 'true',
      };
      updateShipToPricingEligibility({
        transactionAccountId,
        pricingEligibility,
        shipToAddress: { id: existingShipToPartyId ?? (shipToParty?.id as string) },
      });
    };

    const purchaseOrderNumber = purchaseOrder && purchaseOrder.number ? purchaseOrder.number : '';

    if (loading) return <LoadingIndicator />;
    if (error) {
      if (isPartnerAccount) {
        const GenericError = withErrorWrapper(ErrorComponents.GenericErrorPage);
        return <GenericError error={error} />;
      }
      return <ErrorComponents.GenericErrorPage error={error} />;
    }
    return (
      <div className={'invoice-group'} style={{ maxWidth: '600px', padding: '25px' }}>
        <Form onSubmit={handleFormSubmit}>
          {({ formProps, dirty, setFieldValue }) => (
            <form data-testid="edit-invoice-group-form" {...formProps}>
              <FormHeader title="Edit billing profile"></FormHeader>
              <InflightInvoiceBanner transactionAccountId={transactionAccountId} message={inflightBannerMessage} />
              <FormSection>
                {formError ? (
                  <InfoPanel
                    type="error"
                    message={
                      <ErrorComponents.ErrorMessageOnInfoPanel
                        title="We're unable to update the invoice group for the following reason"
                        message={formError}
                        {...(isPartnerAccount && { helpContent: <HelpContent /> })}
                      />
                    }
                  />
                ) : !transactionAccountId ? (
                  <InfoPanel
                    type="warning"
                    title={'Cannot update invoice group'}
                    message={'This invoice group is missing transaction-account id'}
                  />
                ) : null}
              </FormSection>
              <FormSection>
                {isNotValidAddress ? (
                  <InfoPanel
                    type="error"
                    message={
                      isPartnerAccount ? (
                        <ErrorComponents.ErrorMessageOnInfoPanel
                          title="We're unable to edit a new invoice group for the following reason"
                          message={billingAddressError}
                          helpContent={<HelpContent />}
                        />
                      ) : (
                        <ErrorComponents.ErrorMessageOnInfoPanel
                          title="We're unable to edit a new invoice group for the following reason"
                          message={billingAddressError}
                        />
                      )
                    }
                  />
                ) : null}
              </FormSection>
              <SoldToAddressStep
                onFieldValueChange={setFieldValue}
                transactionAccountId={transactionAccountId}
                billToAddress={transactionAccount?.billToParty}
                shipToAddress={shipToAddress ?? shipToParty}
                onShipToAddressUpdate={setShipToAddress}
                billingProfileData={{
                  invoiceGroupName: name,
                }}
                fieldErrors={fieldErrors}
                isPartnerAccount={isPartnerAccount}
              />
              <div className={`${hidePricingEligibilityUpdate ? 'hide-form-section' : ''}`}>
                <FormSection>
                  {' '}
                  <ChangePricingEligibility
                    pricingEligibility={
                      shipToParty?.shipToPriceEligibility ?? transactionAccount?.billToPartyPricingEligibilty
                    }
                  />
                </FormSection>
              </div>
              <InvoiceContactsStep
                onUpdateAdminFieldsDirtyFlag={updateAdminFieldsDirtyFlag}
                defaultRecipients={invoiceRecipients ?? ['']}
              />
              <CurrencyPONumberStep
                isPONumberFeatureEnabled={isPONumberFeatureEnabled}
                isPOMultipleUseChecked={isPOMultipleUseChecked}
                onPOMultipleUseChangeEvent={onPOMultipleUseChangeEvent}
                purchaseOrderNumber={defaultTo(purchaseOrderNumber, '')}
                hideCurrency={true}
              />
              <FormFooter align="end">
                <ButtonGroup>
                  <Button appearance="default" onClick={closeSidebar}>
                    Cancel
                  </Button>
                  <LoadingButton
                    isDisabled={!dirty && !adminFieldsDirty}
                    appearance="primary"
                    type="submit"
                    testId="update-button"
                    isLoading={isProcessing}
                  >
                    Save
                  </LoadingButton>
                </ButtonGroup>
              </FormFooter>
            </form>
          )}
        </Form>
      </div>
    );
  }
);

export default React.memo(EditInvoiceGroupPane);
